import { AEMLink } from '@msc-b2c/interfaces/types';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  AppDispatch,
  closeLoader,
  createAemRegistry,
  fetchRouteAemPageContent,
  getActiveSession,
  membershipsData,
  openLoader,
  RootState,
} from '@msc-b2c/shared-utils';
import { wrappers } from '../wrappers';
import { render } from '@msc-b2c/shared-components';
import { TransformedNode } from '@msc-b2c/interfaces/interfaces';
import { usePrivateAreaPageViewTracking } from '../utils/analyticsUtils';
import { LoginMode } from '@msc-b2c/interfaces/enums';
import { getMembershipData } from '../utils/myAreaUtils';
import { useDispatch, useSelector } from 'react-redux';
import { updateCopyrightYear } from './utils/navigationMenuUtils';

export function NavigationRoute(urlLink: Readonly<AEMLink>) {
  const dispatch = useDispatch<AppDispatch>();
  const { loginMode } = getActiveSession();
  const [htmlContent, setHtmlContent] = useState('');
  const [routeModelJson, setRouteModelJson] = useState<TransformedNode | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [analyticsReady, setAnalyticsReady] = useState(false);
  const membershipRequestInProgress = useRef(false);
  const { membershipData } = useSelector((state: RootState) => state.myCruise);
  const registry = createAemRegistry(false, wrappers as never[]);
  usePrivateAreaPageViewTracking({
    enabled: analyticsReady,
  });

  function injectAnalyticsScript(scriptContent?: string) {
    const previousScriptElement = document.getElementById('analytics-script');
    if (previousScriptElement) {
      previousScriptElement.remove(); // Remove the previous script if it exists
    }
    const script = document.createElement('script');
    script.id = 'analytics-script';
    if (scriptContent) {
      // Remove script tags if they exist
      script.textContent = scriptContent.replaceAll(/<script.*?>/g, '').replace(/<\/script>/g, '');
    }

    document.head.appendChild(script);
    globalThis.containerMappingRefresh(); // Refresh the container mapping after script injection
  }

  useEffect(() => {
    document.querySelector('body')?.classList.toggle('is-navigation-page', true);
  }, []);

  useEffect(() => {
    const fetchHtml = async () => {
      try {
        if (urlLink?.url) {
          openLoader('.loader');

          const baseUrl = urlLink.url.replace('.html', '');
          const htmlUrl = `${baseUrl}/jcr:content/root/container${
            globalThis.location.href.includes('.html') ? `.content.html` : ``
          }`;
          const scriptUrl = `${baseUrl}.mymscanalytics.html`;

          const [html, script] = await Promise.all([
            fetch(htmlUrl).then((res) => res.text()),
            fetch(scriptUrl).then((res) => res.text()),
          ]);

          setHtmlContent(html);
          injectAnalyticsScript(script);
          setAnalyticsReady(true);
        }
      } catch (error) {
        console.error('Error fetching AEM page: ', error);
      }
    };

    if (urlLink?.url) {
      setIsLoading(true);
      setAnalyticsReady(false);

      fetchHtml().then(() =>
        fetchRouteAemPageContent(urlLink?.url).then((data) => {
          setRouteModelJson(data);
          setIsLoading(false);
          closeLoader('.loader');
        })
      );
    }
  }, [urlLink?.url]);

  useLayoutEffect(() => {
    if (isLoading) {
      return;
    }
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [isLoading, urlLink?.url]);

  // Replace the copyright year placeholder in the freshly fetched route HTML,
  // once it has been committed to the DOM.
  useEffect(() => {
    if (htmlContent) {
      updateCopyrightYear();
    }
  }, [htmlContent]);

  useEffect(() => {
    if (membershipRequestInProgress.current || loginMode !== LoginMode.FULL || membershipData) {
      return;
    }

    membershipRequestInProgress.current = true;
    getMembershipData()
      .then((res) => {
        dispatch(membershipsData(res?.memberships ? res : { memberships: [] }));
      })
      .finally(() => {
        membershipRequestInProgress.current = false;
      });
  }, [dispatch, loginMode, membershipData]);

  return (
    <div style={isLoading ? { visibility: 'hidden' } : undefined}>
      <div
        className="cmp-navigation-route"
        data-navigation-route={urlLink?.url}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      ></div>
      {routeModelJson?.nodes?.map(render.bind(null, { registry }))}
    </div>
  );
}
